import { zodResolver } from '@hookform/resolvers/zod'
import { ApiError, loginSchema } from '@mevaser/shared'
import { Alert, Button, Field, Input } from '@mevaser/ui'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import {
  CodeForm,
  type CodeChannel,
  type CodeReason,
  completeSignIn,
  SignInShell,
} from '../components/sign-in-shell.js'
import { api } from '../lib/api.js'

/** Step one: email and password. */
type CodeStep = {
  name: 'code'
  email: string
  codeSent: boolean
  codeReason?: CodeReason
  channel?: CodeChannel
  phoneLast4?: string
}

function CredentialsForm({ onCodeRequired }: { onCodeRequired: (step: CodeStep) => void }) {
  const [error, setError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof loginSchema>>({ resolver: zodResolver(loginSchema) })

  const onSubmit = handleSubmit(async (values) => {
    setError(null)
    try {
      const result = await api.post<{
        status: string
        codeSent?: boolean
        codeReason?: CodeReason
        codeChannel?: CodeChannel
        codePhoneLast4?: string
      }>('/auth/login', values)
      if (result.status === 'email_code_required') {
        onCodeRequired({
          name: 'code',
          email: values.email,
          codeSent: result.codeSent ?? true,
          codeReason: result.codeReason,
          channel: result.codeChannel,
          phoneLast4: result.codePhoneLast4,
        })
      } else {
        completeSignIn()
      }
    } catch (err) {
      // Deliberately shows the server's wording: it never distinguishes a
      // wrong password from an unknown account.
      setError(err instanceof ApiError ? err.message : 'Could not sign in')
    }
  })

  return (
    <>
      {error ? (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Email" error={errors.email?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('email')}
              type="email"
              autoComplete="username"
              autoFocus
              invalid={!!errors.email}
            />
          )}
        </Field>
        <Field label="Password" error={errors.password?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('password')}
              type="password"
              autoComplete="current-password"
              invalid={!!errors.password}
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isSubmitting}>
          Sign in
        </Button>
      </form>
      <p className="mt-4 text-center text-xs">
        <Link
          to="/forgot-password"
          className="text-foreground-muted underline-offset-2 hover:underline"
        >
          Forgot your password?
        </Link>
      </p>
      <p className="mt-3 text-center text-xs text-foreground-subtle">
        This device stays signed in. Use Sign out on a shared terminal.
      </p>
    </>
  )
}

export function LoginPage() {
  const [step, setStep] = useState<{ name: 'credentials' } | CodeStep>({ name: 'credentials' })

  return (
    <SignInShell>
      {step.name === 'credentials' && <CredentialsForm onCodeRequired={setStep} />}
      {step.name === 'code' && (
        <CodeForm
          email={step.email}
          codeSent={step.codeSent}
          codeReason={step.codeReason}
          channel={step.channel}
          phoneLast4={step.phoneLast4}
        />
      )}
    </SignInShell>
  )
}
